import { AgoraWebClient } from "@/utils/agora-web-client";
import { eventRoomStore as globleventroomStore } from "@/stores/event";
import {
  MainLiveStreamId,
  INJECT_STREAM_ID,
  SECOND_INJECT_STREAM_ID,
  SCREEN_SHARE_STREAM_ID,
} from "@/utils/config";
import { useEventRoomState } from "@/containers/root-container";
import { ESocketEvents } from "@/utils/api/sockets.enum";
import * as Sentry from "@sentry/react";

export default function useSubscribeRTCEvent({
  eventRoomStore,
  emitEvent
}: any = globleventroomStore) {
  // const eventRoomState = useEventRoomState();
  const subscribeRTCEvent = (webClient: AgoraWebClient) => {
    webClient.rtc.on("user-joined", async ([remoteUser]) => {
      const streamID = remoteUser.uid;
      // Instrumented like user-published: the guide-reconnect incidents
      // (2026-07-07) were invisible because joined/left were never logged.
      console.log("[rtc-event] user-joined", { uid: streamID });
      Sentry.addBreadcrumb({
        category: "agora-rtc",
        message: "user-joined",
        level: "info",
        data: { uid: streamID },
      });
      let tempRemoteStream = {
        audioTrack: null,
        videoTrack: null,
      } as any;

      const userRole = eventRoomStore.getUserRoleById(streamID);
      const details = {
        name:
          userRole == "multiconnection"
            ? await eventRoomStore.fetchUserDetails(
              eventRoomStore.state.me.channelName,
              streamID
            )
            : eventRoomStore.getUserNameById(streamID),
      };

      const urlParams = new URLSearchParams(window.location.search);
      const showAdminVideoTile = urlParams.get('show');

      if(eventRoomStore._state.me.info.role == 'admin'){
        if(showAdminVideoTile == '1'){
          emitEvent(ESocketEvents.ADMIN_TILE, {
            visit_id: eventRoomStore._state.me.channelName,
            value: true
          });
        } else {
          emitEvent(ESocketEvents.ADMIN_TILE, {
            visit_id: eventRoomStore._state.me.channelName,
            value: false
          });
        }
      }


      console.log("adding remote stream details ?????????: ", tempRemoteStream)
      eventRoomStore.addRemoteStreamDetails(
        streamID,
        tempRemoteStream,
        details,
        userRole
      );
    });

    webClient.rtc.on("user-left", ([user, reason]) => {
      // reason matters: "Quit" = clean leave; "ServerTimeOut" = network drop —
      // a LATE timeout arriving around the publisher's rejoin can destroy the
      // just-restored stream entry (the receive-side silence class).
      console.log("[rtc-event] user-left", { uid: user.uid, reason });
      Sentry.addBreadcrumb({
        category: "agora-rtc",
        message: "user-left",
        level: "info",
        data: { uid: user.uid, reason },
      });
      handleStreamRemove(user.uid);
    });

    webClient.rtc.on("user-published", async ([remoteUser, mediaType]) => {
      const streamID = remoteUser.uid;

      console.log("[rtc-flow] user-published received", {
        uid: streamID,
        mediaType,
        role: eventRoomStore.state.me.info.role,
        channel: eventRoomStore.state.me.channelName,
      });
      console.log("[rtc-event] user-published", { uid: streamID, mediaType });
      Sentry.addBreadcrumb({
        category: "agora-rtc",
        message: "user-published",
        level: "info",
        data: { uid: streamID, mediaType },
      });

      //if (streamID !== eventRoomStore.state.me.uid) {
      try {
        await webClient.subscribe(remoteUser, mediaType);
        console.log("[rtc-event] subscribe OK", { uid: streamID, mediaType });
      } catch (err) {
        console.error("[RTC] subscribe failed", { uid: streamID, mediaType, err });
        Sentry.captureException(err, {
          tags: { component: "agora-rtc", op: "subscribe" },
          extra: { uid: streamID, mediaType },
        });
      }

      let streamTrack =
        mediaType === "video" ? remoteUser.videoTrack : remoteUser.audioTrack;
      let streamTrackType = mediaType === "video" ? "videoTrack" : "audioTrack";
      const userRole = eventRoomStore.getUserRoleById(streamID);
      const details = {
        name:
          userRole == "multiconnection"
            ? await eventRoomStore.fetchUserDetails(
              eventRoomStore.state.me.channelName,
              streamID
            )
            : eventRoomStore.getUserNameById(streamID),
      };

      eventRoomStore.updateRemoteDetails(
        streamTrackType,
        streamID,
        streamTrack,
        details
      );
      //});
    });
    webClient.rtc.on("user-unpublished", ([remoteUser, mediaType]) => {
      console.log("[rtc-event] user-unpublished", { uid: remoteUser.uid, mediaType });
      Sentry.addBreadcrumb({
        category: "agora-rtc",
        message: "user-unpublished",
        level: "info",
        data: { uid: remoteUser.uid, mediaType },
      });
      eventRoomStore.mute(remoteUser.uid, mediaType);
      // Defensive fix: when video is unpublished (e.g. mobile does unpublish/republish),
      // also reset videoTurnedOn to prevent stale RTM state from triggering play on a dead track
      if (mediaType === "video") {
        eventRoomStore.mute(remoteUser.uid, "videoTurnedOn");
      }
    });
  };

  const handleStreamRemove = (streamID: number) => {
    const pinid = Number(sessionStorage.getItem("pinid"));
    if (
      eventRoomStore.state.rtc.injectedStream &&
      +streamID === +eventRoomStore.state.rtc.injectedStream.streamID
    ) {
      eventRoomStore.removeInjectedStream();
    } else {
      eventRoomStore.removePeerUser(streamID);
      eventRoomStore.removeRemoteStream(streamID);
    }
    if (streamID === eventRoomStore.state.me.mainLiveStreamID) {
      eventRoomStore.updateLocalMe({
        mainLiveStreamID: 0,
      });
    }
    if (streamID === SCREEN_SHARE_STREAM_ID) {
      const grid = sessionStorage.getItem("grid");
      if (grid === "true") {
        eventRoomStore.updateGridView(true);
      } else {
        eventRoomStore.updateGridView(false);
      }
    }

    if (streamID === pinid) {
      // sessionStorage.setItem("pinid", "0");
      eventRoomStore.setMeAttr("mainLiveStreamID", 0);
    }
  };

  return {
    subscribeRTCEvent,
  };
}
